import React, { useEffect, useState } from "react";
import OrderPaperworkEditor from "./OrderPaperworkEditor";
import { getPaperworkItem } from "../../services/orderPaperwork";
import { isEditableForm, paperworkLoadError } from "../../utils/paperworkForms";

// A paperwork task's "Open in chart", for someone without the Orders button:
// the form they were handed, straight into the editor, to fill in and save for
// the doctor, who signs it from the Paperwork tab. Rendered inline in the
// chart, inside #clinic-root, where its Tailwind applies.
export default function PaperworkLinkEditor({ patientId, paperworkId, onClose }) {
  const [form, setForm] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getPaperworkItem({ patientId, paperworkId })
      .then((res) => { if (!cancelled) setForm(res?.form || null); })
      .catch((err) => { if (!cancelled) setError(paperworkLoadError(err)); });
    return () => { cancelled = true; };
  }, [patientId, paperworkId]);

  const note = (text) => (
    <div className="m-auto max-w-md text-center text-sm text-slate-600 dark:text-slate-300">
      <p>{text}</p>
      <button type="button" onClick={onClose}
              className="mt-3 text-xs px-3 py-1.5 rounded-lg ring-1 ring-slate-300 dark:ring-slate-600">
        Close
      </button>
    </div>
  );

  // Straight from the click: the server's link was just signed with the form.
  const openSigned = () => {
    if (form?.url) window.open(form.url, "_blank", "noopener,noreferrer");
    else setError("Couldn't open the form — open it from your task in Messages.");
  };

  let body;
  if (error) {
    body = note(error);
  } else if (form?.status === "signed") {
    // Refilling would replace the signed file with an unsigned one.
    body = (
      <div className="m-auto max-w-md text-center text-sm text-slate-600 dark:text-slate-300">
        <p>
          {form.name} is signed{form.signer_name ? ` by ${form.signer_name}` : ""}, so it can&apos;t be changed here.
        </p>
        <div className="mt-3 flex justify-center gap-2">
          <button type="button" onClick={openSigned}
                  className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 text-white">
            Open to view or print
          </button>
          <button type="button" onClick={onClose}
                  className="text-xs px-3 py-1.5 rounded-lg ring-1 ring-slate-300 dark:ring-slate-600">
            Close
          </button>
        </div>
      </div>
    );
  } else if (!form) {
    body = <div role="status" className="m-auto text-sm text-slate-500 dark:text-slate-400">Loading the form…</div>;
  } else if (!isEditableForm(form)) {
    body = note(`${form.name} can't be refilled here — it was filled in by hand or already generated. `
      + "Open it from your task in Messages to view or print it, or to upload a filled version.");
  } else {
    body = (
      <OrderPaperworkEditor
        patientId={patientId}
        doc={{ path: form.original_path || form.path, filename: form.name }}
        paperworkId={form.id}
        allowUnsigned
        canSign={false}
        backLabel="✕ Close"
        onBack={onClose}
      />
    );
  }

  return (
    <div role="dialog" aria-modal="true" aria-label="Order paperwork"
         className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      {/* The Orders window's colours: its own text colour, or it inherits the
          chart's and reads dark-on-dark in the dark theme. */}
      <div data-testid="paperwork-link-panel"
           className="flex h-[90vh] w-full max-w-6xl flex-col rounded-xl bg-white p-4 shadow-xl text-slate-900 dark:bg-[#0a0a0f] dark:text-slate-100">
        {body}
      </div>
    </div>
  );
}
